import React, { useState, useEffect, useContext, } from 'react';
import { Form, Select, Row, Col, Button, Descriptions, Space, Typography, Alert, Input, Divider, Empty, } from 'antd';
import { LockFilled, UnlockFilled,} from '@ant-design/icons';
import { PermissionsContext } from '../../../router/DashboardRoutes';
import { Modal } from '../../UI/Modal/Modal.component';
import "../../../App.css"
import { getDeviceEthers, saveEthers } from '../../../services/StationService';

const { Text } = Typography;

export const VLANTableModal = ({visible, setVisible, device, profileCombination, ...props}) => {

    const ETHER_MAX_COUNT = 16;
    const CLEAR_PROFILE_ID = 1;
    
    const { POS, Dispenser } = profileCombination || {};

    const [form] = Form.useForm();

    const userPermissions = useContext(PermissionsContext)
    const { edit: allowEditDevice } = userPermissions.device;

    const [dataSource, setDataSource] = useState({});
    const [editMode, setEditMode] = useState(false);
    const [hasCustomSettings, setHasCustomSettings] = useState(false);
    const [displayResetWarning, setDisplayResetWarning] = useState(false);
    const [showSuccessMessage, setShowSuccessMessage] = useState(false);
    const [refresh, setRefresh] = useState(true);

    const btnSize = 'middle';

    useEffect(() => {
        if(editMode){
            setShowSuccessMessage(false);
            form.resetFields();
        }
    }, [editMode, form]);

    useEffect(() => {
        if(showSuccessMessage){
            setTimeout(() => setShowSuccessMessage(false), 6000)
        }
    }, [showSuccessMessage])

    useEffect(() => {

        let data = {
            deviceID: device.id, 
            ownerID: device.ownerID, 
            pos: POS?.id || CLEAR_PROFILE_ID, 
            dispenser: Dispenser?.id || CLEAR_PROFILE_ID, 
        };

        if(refresh){
            getDeviceEthers(data).then(function (response) {
                if(response.result){
                    setDataSource(response.data)
                } else {
                    if(response.message === 'Ether configuration not found'){
                        setDataSource({});
                    }
                }
                setRefresh(false);
                setDisplayResetWarning(false);
            })
        }
    }, [refresh, POS, Dispenser, device])

    useEffect(() => {

        const getEtherValues = (data) => {

            let ethers = {};

            if(data){
                for (let i = 1; i <= Object?.keys(data)?.length ; i++){
                    const propName = `ether${i}`;

                    if(data[i]){
                        ethers[propName] = data[i]?.value || 'TRUNK'; // Ether 1 has no value, set to "TRUNK"
                    } else {
                        ethers[propName] = 'N/A'
                    }
                } 
            } else {
                for (let i = 1; i <= ETHER_MAX_COUNT; i++){
                    const propName = `ether${i}`;
                    ethers[propName] = 'N/A'
                }
            }
            
            form.setFieldsValue(ethers);
        }

        if(dataSource){
            if(dataSource?.customEthers){
                setHasCustomSettings(true);
            }
            
            if(!displayResetWarning){
                if(dataSource?.customEthers){
                    getEtherValues(dataSource?.customEthers)
                } else {
                    getEtherValues(dataSource?.defaultEthers)
                }
            } else {
                getEtherValues(dataSource?.defaultEthers)
            }
        } else {
            getEtherValues()
        }
    }, [displayResetWarning, form, editMode, dataSource])

    const updateEthers = (values) => {

        const customEthersObj = {};

        Object.values(values).forEach((ether, index) => {
            if (index < ETHER_MAX_COUNT){                
                const vlan = dataSource?.vlanOptions?.find((option) => option.value === ether)
                if(index === 0){
                    customEthersObj[index + 1] = {
                        value: '', 
                        vlanName: 'TRUNK',
                    }
                } else {
                    customEthersObj[index + 1] = {
                        value: vlan.value, 
                        vlanName: vlan.name,
                    }
                }
            }
        })

        let newSettings = {
            deviceID: device.id, 
            ownerID: device.ownerID, 
            data: customEthersObj,
        }

        saveEthers(newSettings).then(function (response) {
            if(response.result){
                setEditMode(false);
                setRefresh(true);
                setShowSuccessMessage(true);
            }
        }) 
    }

    const renderFormFields = () => {

        let fields = [];

        for(let i = 1; i <= ETHER_MAX_COUNT ; i++){
            fields.push(
                <Form.Item 
                    {...formItemLayout} 
                    key={i} 
                    label={`Ether ${i}`} 
                    name={`ether${i}`} 
                    style={{
                        marginBottom: !editMode ? 8 : undefined, 
                        fontWeight: !editMode ? 600 : undefined,
                    }}
                    >
                    <Select
                        showSearch
                        optionFilterProp="children"
                        filterOption={(input, option) => (option?.label.toLowerCase() ?? '').includes(input.toLowerCase())}
                        filterSort={(optionA, optionB) =>
                            (optionA?.label ?? '').toLowerCase().localeCompare((optionB?.label ?? '').toLowerCase())
                        }
                        disabled={i===1 || !editMode || displayResetWarning}
                        bordered={editMode}
                        showArrow={editMode}
                        options={dataSource?.vlanOptions?.map((vlan) => ({
                            label: vlan.name, 
                            value: vlan.value, 
                            key: vlan.value
                        }))
                        }
                    />
                </Form.Item>
            )
        }
        return fields;
    }

    const formItemLayout = {
        labelCol: {
            span: 6,
        },
        labelAlign: 'left',
        wrapperCol: {
            span: 18,
        },
    };

    return (
        <>
            <Modal
                visible={visible}
                setVisible={setVisible}
                title='VLAN Table'
                width={700}
                loading={refresh}
                extra={(allowEditDevice && Object.keys(dataSource).length > 0) && <Button className={!editMode ? 'btn__secondary--dark-gray' : undefined} onClick={() => setEditMode(!editMode)} icon={editMode ? <LockFilled/> : <UnlockFilled/>}>{editMode ? 'LOCK' : 'UNLOCK'}</Button>}
                actionGroup={false}
                >
                <>
                    <Space direction='vertical' size='middle'>
                        <Descriptions 
                            column={1} 
                            size='small'
                            styles={{
                                    label: {fontWeight: 600}
                                }}
                            >
                                <Descriptions.Item label='Serial Number'>#{device?.serialNum}</Descriptions.Item>
                                <Descriptions.Item label='POS'>{(!POS || POS === CLEAR_PROFILE_ID) ? 'None' : POS?.name}</Descriptions.Item>
                                <Descriptions.Item label='Dispenser'>{(!Dispenser || Dispenser === CLEAR_PROFILE_ID) ? 'None' : Dispenser?.name}</Descriptions.Item>
                        </Descriptions>

                        { Object.keys(dataSource).length > 0 ?
                            <>
                                { showSuccessMessage && <Alert type='success' showIcon message='Your settings have been updated!'/> }
                                { !editMode && <Divider style={{margin: 0}}/> }
                                <Form
                                    form={form}
                                    autoComplete='off'
                                    id={`device-${device.id}-edit-ports-form`}
                                    size='small'
                                    layout='horizontal'
                                    onFinish={(values) => updateEthers(values)}
                                    onFieldsChange={() => setShowSuccessMessage(false)}
                                >
                                    { (editMode && displayResetWarning) &&
                                        <>
                                            <Form.Item>
                                                <Alert 
                                                    description={`Resetting this device to default configuration will remove any customized settings, and restore this device to its initial configuration. Are you sure you want to proceed?`}
                                                    showIcon={true}
                                                    type='warning'
                                                    />
                                            </Form.Item>
                                            <Text strong>Review changes to be applied: <br/></Text><br/>
                                        </>
                                    }

                                    <Row gutter={[12,12]}>
                                        <Col span={24}>
                                            <Row gutter={[16,16]}>
                                                <Col span={12}>
                                                    {renderFormFields().slice(0, 8)}
                                                </Col>
                                                <Col span={12}>
                                                    {renderFormFields().slice(8)}
                                                </Col>
                                            </Row>
                                        </Col>

                                        { editMode && 
                                            <>
                                                <Col span={24}>
                                                    {!displayResetWarning ? 
                                                        <Form.Item noStyle>
                                                            <Row justify='space-between'>
                                                                <div>
                                                                    {hasCustomSettings && <Button size={btnSize} danger onClick={() => setDisplayResetWarning(true)}>Reset Configuration</Button>}
                                                                </div>
                                                                <Space>
                                                                    <Button size={btnSize} onClick={() =>  {form.resetFields(); setEditMode(false)}}>Cancel</Button>                                                        
                                                                    <Button htmlType='submit' size={btnSize} type='primary'>Save</Button>
                                                                </Space>
                                                            </Row>
                                                        </Form.Item>
                                                    : 
                                                        <>
                                                            <Space direction='vertical' size='middle' style={{width: '100%'}}>
                                                                <Text>Reset to default configuration. Please type "RESET" to confirm:</Text>
                                                                <Form.Item noStyle
                                                                    name='update'
                                                                    rules={[
                                                                        {
                                                                            required: true,
                                                                            validateTrigger: 'onSubmit',
                                                                            validator: (_, value) => {
                                                                                    if(value === 'RESET'){
                                                                                        return Promise.resolve();
                                                                                    }
                                                                                    return Promise.reject(new Error('Please enter "RESET" if you want to proceed.'));
                                                                                }
                                                                            }
                                                                        ]}
                                                                    >
                                                                    <Input size='middle' onInput={(e) => e.target.value = e.target.value.toUpperCase()}/>
                                                                </Form.Item>
                                                                <Row justify='end'>
                                                                    <Form.Item noStyle>
                                                                        <Space>
                                                                            <Button size={btnSize} onClick={() => setDisplayResetWarning(false)}>Cancel</Button>
                                                                            <Button size={btnSize} htmlType='submit' type='primary'>Reset Configuration</Button>
                                                                        </Space>
                                                                    </Form.Item>
                                                                </Row>
                                                            </Space>
                                                        </>
                                                    }
                                                </Col>
                                            </>
                                        }
                                    </Row>
                                </Form>
                            </>
                            : 
                            <>
                                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description='No data available.' />
                            </>
                        }
                    </Space>
                </>
            </Modal>
        </>
    )
}